import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

interface SideChoiceProps {
  values: Array<{ slug: string; label: string }>;
  selected: string | null;
  disabledValues: Set<string>;
  onSelect: (slug: string) => void;
}

/**
 * `Strona` — corner sofa handedness — is the classic furniture-retail mirror choice, and a
 * text dropdown wastes it. Two mirrored diagrams instead, which is also what the 29 shared
 * EANs represent: a left and a right unit shipping under one barcode.
 */
export function SideChoice({ values, selected, disabledValues, onSelect }: SideChoiceProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div role="radiogroup" aria-label={t('product.sideHelp')} className="flex gap-3">
      {values.map((value) => {
        const isLeft = /lew|ліва|left/i.test(value.slug + value.label);
        const disabled = disabledValues.has(value.slug);
        const isSelected = selected === value.slug;
        return (
          <button
            key={value.slug}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-disabled={disabled}
            disabled={disabled}
            onClick={() => !disabled && onSelect(value.slug)}
            className="group flex flex-1 flex-col items-center gap-2 p-3 transition-colors duration-200"
            style={{
              background: isSelected ? 'var(--color-ground-lift)' : 'transparent',
              boxShadow: isSelected
                ? 'inset 0 0 0 2px var(--color-brand)'
                : 'inset 0 0 0 1px var(--color-line)',
              opacity: disabled ? 0.35 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <svg
              viewBox="0 0 64 44"
              className="h-11 w-16"
              aria-hidden="true"
              style={{ transform: isLeft ? undefined : 'scaleX(-1)' }}
            >
              {/* Plan view of a corner sofa: long run plus the return arm. */}
              <rect x="4" y="26" width="44" height="14" rx="2" fill="var(--color-ink-soft)" />
              <rect x="4" y="6" width="14" height="22" rx="2" fill="var(--color-brand)" />
              <rect x="4" y="22" width="44" height="5" rx="1.5" fill="var(--color-muted)" />
              <rect x="48" y="26" width="6" height="14" rx="2" fill="var(--color-muted)" />
            </svg>
            <span className="text-xs font-semibold">{value.label}</span>
            {disabled && <span className="sr-only">{t('product.unavailableCombination')}</span>}
          </button>
        );
      })}
    </div>
  );
}
